import { useInvoiceDetailQuery } from '@/app/services/invoiceAPI';
import { formatStringToReal } from '@helpers/currency';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { red } from '@mui/material/colors';
import { styled } from '@mui/material/styles';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface StandardDialogProps {
  title: string;
  content: string;
  open: boolean;
  onClose: () => void;
}

export default function TableDialog(props: StandardDialogProps) {
  const { title, content, open, onClose } = props;
  const { t } = useTranslation(['invoices', 'common']);
  const theme = useTheme();

  const { data: detail } = useInvoiceDetailQuery({ id: content }, { skip: !content });

  const [expanded, setExpanded] = useState<string | false>(false);

  const SubHeaderTableCell = styled(TableCell)({
    fontSize: 13,
    fontWeight: 'bold',
    backgroundColor: '#FFFFFF',
  });

  const RowTableCell = styled(TableCell)({
    fontSize: 13,
  });

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Dialog
      open={open}
      maxWidth="xl"
      fullWidth
      onClose={(event: React.SyntheticEvent, reason: string) => {
        if (reason === 'escapeKeyDown') {
          onClose();
        }
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{
        sx: {
          height: '70vh',
          maxHeight: '900px',
          minHeight: '500px',
        },
      }}
    >
      <DialogTitle id="alert-dialog-title" sx={{ fontSize: 18, fontWeight: 600 }}>
        {title}
      </DialogTitle>
      <DialogContent>
        <Box
          component="form"
          sx={{ '& .MuiTextField-root': { m: 1, width: '25ch' }, mb: 3 }}
          noValidate
          autoComplete="off"
        >
          <div>
            <TextField
              disabled
              id="detail.invoice.code"
              label={t('detail.invoice.code')}
              size="small"
              value={detail?.code || ' '}
            />
            <TextField
              disabled
              id="detail.invoice.erp.code"
              label={t('detail.invoice.erp.code')}
              value={detail?.codeErp || ' '}
              size="small"
            />
            <TextField
              disabled
              id="detail.invoice.branch"
              label={t('detail.invoice.branch')}
              value={detail?.branch || ' '}
              size="small"
            />
          </div>
        </Box>

        <Box
          width="100%"
          mt={5}
          sx={{
            maxHeight: '500px',
            overflowY: 'auto',
            paddingRight: '4px',
          }}
        >
          {detail?.costCenters?.map((costCenter) => (
            <Accordion
              key={costCenter.code}
              disableGutters
              elevation={1}
              expanded={expanded === costCenter.code}
              onChange={handleChange(costCenter.code)}
              sx={{
                marginBottom: '6px',
                border: '1px solid #e0e0e0',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: '#e7e7e7ce',
                  borderBottom: '1px solid #E5E0D0',
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    gap: 2,
                  },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#555' }}>
                  {t('project.result.center')}: {costCenter.code}
                </Typography>

                <Box
                  sx={{
                    backgroundColor: '#F39C12',
                    color: 'white',
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    fontWeight: 'bold',
                  }}
                >
                  {costCenter.items?.length || 0}
                </Box>

                {!costCenter.isValid && (
                  <Tooltip title={t('project.not.registered.or.inactive')} arrow placement="right">
                    <ErrorIcon fontSize="medium" sx={{ color: red[500] }} />
                  </Tooltip>
                )}
              </AccordionSummary>

              <AccordionDetails sx={{ padding: 0 }}>
                <TableContainer>
                  <Table size="small" aria-label="items table">
                    <TableHead>
                      <TableRow>
                        <SubHeaderTableCell style={{ minWidth: 100 }}>{t('common:item.type')}</SubHeaderTableCell>
                        <SubHeaderTableCell style={{ minWidth: 120 }}>{t('common:price')}</SubHeaderTableCell>
                        <SubHeaderTableCell>{t('common:description')}</SubHeaderTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {costCenter.items?.map((row, index) => (
                        <TableRow
                          key={row.id.toString()}
                          sx={{ backgroundColor: index % 2 === 0 ? '#F9F9F9' : '#FFFFFF' }}
                        >
                          <RowTableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {row.type}
                              {costCenter.isValid === false && (
                                <Tooltip title={t('product.type.not.registered')} arrow placement="right">
                                  <ErrorIcon fontSize="small" sx={{ color: red[500] }} />
                                </Tooltip>
                              )}
                            </Box>
                          </RowTableCell>
                          <RowTableCell>{formatStringToReal(row.price)}</RowTableCell>
                          <RowTableCell>{row.person}</RowTableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ display: 'flex', gap: 1, p: 3, borderTop: 1, borderTopColor: '#E4E4E4' }}>
        <Button variant="secondary" onClick={onClose}>
          {t('common:close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
